// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import dayjs, { Dayjs } from 'dayjs';
import React from 'react';
import {
  Typography,
  Box,
  Select,
  SelectChangeEvent,
  OutlinedInput,
  MenuItem,
  TextField,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { CollateralAgreement } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Model/Collateral';
import { UserService } from '@daml.js/utility-collateral-app-v1/lib/Utility/Collateral/App/Service/User';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import TextInput, { TextInputLabel } from '../../../components/Form/TextInput';
import { InfoWithPopover } from '../../../components/InfoWithPopover';
import { useExerciseSubmission } from '../../../hooks/mutations/ledger/useSubmission';
import { useCollateralUserService } from '../../../hooks/queries/collateral/onboarding/useUserService';
import { ERR_OFFER_COL, SUC_OFFER_COL } from '../../../utils/strings';
import { AssetSelect } from '../AssetSelect';
import { useEligibleInstruments } from '../useEligibleInstruments';
import { CollateralAgreementWithoutState } from './types';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  collateralAgreementPayloads?: CollateralAgreementWithoutState[];
  collateralAgreementPayload?: CollateralAgreementWithoutState;
}
export const OfferCollateralDialog: React.FC<DialogProps> = ({
  collateralAgreementPayloads,
  collateralAgreementPayload,
  open,
  onClose,
}) => {
  const userService = useCollateralUserService();
  const [reference, setReference] = React.useState('');
  const eligibleInst = useEligibleInstruments();
  const defaultAllocateDateTime = dayjs().add(3, 'hours');
  const defaultSettleDateTime = dayjs().add(6, 'hours');

  const [allocationDate, setAllocationDate] = React.useState<Dayjs>(defaultAllocateDateTime);
  const [settleDate, setSettleDate] = React.useState<Dayjs>(defaultSettleDateTime);
  const isValidDates =
    dayjs(allocationDate).isBefore(dayjs(settleDate)) &&
    dayjs(allocationDate).isAfter(dayjs()) &&
    dayjs(settleDate).isAfter(dayjs());

  const [collateralAgreement, setCollateralAgreement] = React.useState<
    CollateralAgreementWithoutState | undefined
  >(collateralAgreementPayload);
  React.useEffect(() => {
    setCollateralAgreement(collateralAgreementPayload);
  }, [collateralAgreementPayload, open]);

  const { submit: transferCollateral } = useExerciseSubmission(
    UserService.UserService_TransferCollateral,
    SUC_OFFER_COL,
    ERR_OFFER_COL,
  );

  const handleSubmit = React.useCallback(async () => {
    if (!userService.data) throw new Error('Collateral user service not found');
    if (!collateralAgreement) throw new Error('No Collateral Agreement selected');
    const positions = eligibleInst.eligibleInstruments.map((i) => ({
      instrument: { admin: i.admin, id: i.id },
      amount: i.amount || '0',
    }));
    const now = new Date();
    now.setTime(now.getTime());

    await transferCollateral({
      cid: userService.data.contractId,
      argument: {
        cid: collateralAgreement.contractId as ContractId<CollateralAgreement>,
        positions,
        reference,
        createdAt: dayjs().toISOString(),
        allocateBefore: allocationDate.toISOString(),
        settleBefore: settleDate.toISOString(),
      },
    });
    onClose();
  }, [
    transferCollateral,
    userService.data,
    reference,
    onClose,
    collateralAgreement,
    eligibleInst.eligibleInstruments,
    allocationDate,
    settleDate,
  ]);
  const onCancel = () => {
    onClose();
    eligibleInst.resetList();
  };

  const eligibleInstrumentsFromAgreement =
    collateralAgreement?.payload.terms.eligibleCollateral.eligibleInstruments;

  const handleAgreementChange = (event: SelectChangeEvent<typeof collateralAgreement>) => {
    if (!collateralAgreementPayloads?.length) {
      return;
    }
    const {
      target: { value },
    } = event;
    setCollateralAgreement(
      collateralAgreementPayloads.find((agreement) => agreement.payload.id === value),
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <FormDialog
        submitName="Offer"
        onCancel={onCancel}
        open={open}
        title="Instruct collateral"
        onSubmit={handleSubmit}
        isSubmitDisabled={!isValidDates}
      >
        {collateralAgreementPayload ? (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <TextInputLabel label="Agreement Id" />
            <Typography variant="body2">{collateralAgreementPayload.payload.id}</Typography>
            <Box sx={{ mt: 2 }}>
              <TextInputLabel label="Party A" />
              <Box
                sx={{
                  mb: 0,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {collateralAgreementPayload.payload.partyA}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextInputLabel label="Party B" />
              <Box
                sx={{
                  mb: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                  }}
                >
                  {collateralAgreementPayload.payload.partyB}
                </Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 1 }}>
              <TextInputLabel label="Collateral Agreement" required />
            </Box>
            <Select
              size="small"
              value={collateralAgreement}
              onChange={handleAgreementChange}
              input={<OutlinedInput id="select-chip" label="Chip" />}
              sx={{ mb: 2 }}
            >
              {(collateralAgreementPayloads || []).map((agreement) => (
                <MenuItem key={agreement.contractId} value={agreement.payload.id}>
                  {agreement.payload.id}
                </MenuItem>
              ))}
            </Select>
            {collateralAgreement && (
              <Box sx={{ mt: 2 }}>
                <TextInputLabel label="Party A" />
                <Box
                  sx={{
                    mb: 0,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {collateralAgreement?.payload.partyA}
                  </Typography>
                </Box>
              </Box>
            )}
            {collateralAgreement && (
              <Box sx={{ mt: 2 }}>
                <TextInputLabel label="Party B" />
                <Box
                  sx={{
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {collateralAgreement?.payload.partyB}
                  </Typography>
                </Box>
              </Box>
            )}
          </>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
          <Box sx={{ mb: 1 }}>
            <TextInputLabel
              endAdornment={
                <InfoWithPopover
                  popoverContent={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2">
                        The timeframe for allocating funds to enable settlement.
                      </Typography>
                    </Box>
                  }
                />
              }
              label="Allocation date"
              required
            />
          </Box>
          <DateTimePicker
            // eslint-disable-next-line react/jsx-props-no-spreading
            renderInput={(props) => <TextField size="small" {...props} />}
            value={allocationDate}
            onChange={(newValue) => {
              if (newValue) {
                setAllocationDate(newValue);
              }
            }}
          />
          {!isValidDates && (
            <Typography variant="caption" color="error">
              Date must be in the future and allocation date must be before settle date
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', mb: 1 }}>
          <Box sx={{ mb: 1 }}>
            <TextInputLabel
              label="Settle date"
              required
              endAdornment={
                <InfoWithPopover
                  popoverContent={
                    <Box sx={{ p: 1 }}>
                      <Typography variant="body2">The timeframe for settling the funds.</Typography>
                    </Box>
                  }
                />
              }
            />
          </Box>
          <DateTimePicker
            // eslint-disable-next-line react/jsx-props-no-spreading
            renderInput={(props) => <TextField size="small" {...props} />}
            value={settleDate}
            onChange={(newValue) => {
              if (newValue) {
                setSettleDate(newValue);
              }
            }}
          />
          {!isValidDates && (
            <Typography variant="caption" color="error">
              Date must be in the future and allocation date must be before settle date
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ mb: 1 }}>
            <TextInputLabel label="Collateral" required />
          </Box>
        </Box>
        <AssetSelect
          onAdd={eligibleInst.addEntry}
          onRemove={eligibleInst.removeEntry}
          rows={eligibleInst.eligibleInstruments}
          updateEntry={eligibleInst.updateEntry}
          eligibleInstruments={eligibleInstrumentsFromAgreement || []}
        />
        <TextInput
          enableTopLabel
          placeholder="Enter an identifier for this pledge"
          label="Instruct Id"
          value={reference}
          setValue={setReference}
        />
      </FormDialog>
    </LocalizationProvider>
  );
};
